import { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useGetUser, useBanUser, useUnbanUser } from "@src/presentation/hooks/users"
import { useRoles, useAssignRolesToUser, useRemoveRolesFromUser } from "@src/presentation/hooks/rbac"
import { ToastService } from "@src/shared/services/toast.service"
import { ProtectedComponent } from "@src/presentation/components/ProtectedComponent"
import { PERMISSIONS } from "@src/shared/constants/permissions"
import { usePermission } from "@src/presentation/hooks/usePermission"
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Chip,
    Divider,
    Avatar,
    Spinner,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useDisclosure,
    CheckboxGroup,
    Checkbox
} from "@heroui/react"
import {
    ChevronLeft,
    User,
    Mail,
    Shield,
    Clock,
    Calendar,
    AlertCircle,
    CheckCircle,
    XCircle,
    Ban,
    Edit,
    Phone,
    Lock,
    ShieldCheck,
    UserCheck,
    UserX,
    Plus
} from "lucide-react"

export default function UserDetailPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { hasPermission } = usePermission()
    const canManageRoles = hasPermission(PERMISSIONS.USER_ASSIGN_ROLE)

    const { isOpen: isBanModalOpen, onOpen: onBanModalOpen, onClose: onBanModalClose } = useDisclosure()
    const { isOpen: isUnbanModalOpen, onOpen: onUnbanModalOpen, onClose: onUnbanModalClose } = useDisclosure()
    const { isOpen: isRoleModalOpen, onOpen: onRoleModalOpen, onClose: onRoleModalClose } = useDisclosure()

    // Fetch roles for role management
    const { data: rolesData } = useRoles()
    const roles = rolesData || []

    // Role assignment mutations
    const { mutate: assignRoles, isPending: isAssigningRoles } = useAssignRolesToUser({
        onSuccess: () => {
            ToastService.success("Roles assigned successfully")
            setSelectedRolesToAssign([])
            refetchUser()
            onRoleModalClose()
        },
        onError: (error) => {
            ToastService.error(error.message || "Failed to assign roles")
        }
    })
    const { mutate: removeRoles, isPending: isRemovingRoles } = useRemoveRolesFromUser({
        onSuccess: () => {
            ToastService.success("Roles removed successfully")
            setSelectedRolesToRemove([])
            refetchUser()
            onRoleModalClose()
        },
        onError: (error) => {
            ToastService.error(error.message || "Failed to remove roles")
        }
    })

    // Permission-protected mutation wrappers
    const handleAssignRoles = (data: { userId: string; roleIds: string[] }) => {
        if (!canManageRoles) {
            ToastService.error("You don't have permission to assign roles")
            return
        }
        assignRoles(data)
    }

    const handleRemoveRoles = (data: { userId: string; roleIds: string[] }) => {
        if (!canManageRoles) {
            ToastService.error("You don't have permission to remove roles")
            return
        }
        removeRoles(data)
    }

    // State for role management
    const [selectedRolesToAssign, setSelectedRolesToAssign] = useState<string[]>([])
    const [selectedRolesToRemove, setSelectedRolesToRemove] = useState<string[]>([])

    // Fetch user data
    const { data: user, isLoading, error, refetch: refetchUser } = useGetUser(id)

    // Ban/unban mutations
    const { mutate: banUser, isPending: isBanning } = useBanUser({
        onSuccess: () => {
            ToastService.success("User banned successfully")
            onBanModalClose()
        },
        onError: (error) => {
            ToastService.error(error.message || "Failed to ban user")
        }
    })

    const { mutate: unbanUser, isPending: isUnbanning } = useUnbanUser({
        onSuccess: () => {
            ToastService.success("User unbanned successfully")
            onUnbanModalClose()
        },
        onError: (error) => {
            ToastService.error(error.message || "Failed to unban user")
        }
    })

    // Loading state
    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <Spinner size="lg" color="primary" />
                    <p className="mt-4 text-default-500">Loading user details...</p>
                </div>
            </div>
        )
    }

    // Error state
    if (error || !user) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-background via-background to-default-50 flex items-center justify-center p-4">
                <Card className="max-w-md shadow-2xl border-0 bg-gradient-to-br from-background to-default-50/50">
                    <CardBody className="text-center p-8">
                        <div className="p-4 rounded-full bg-danger/10 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                            <AlertCircle className="h-10 w-10 text-danger" />
                        </div>
                        <h2 className="text-xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent mb-2">User Not Found</h2>
                        <p className="text-default-500 mb-6">
                            {error?.message || "The user you're looking for doesn't exist or has been removed."}
                        </p>
                        <Button
                            color="primary"
                            onPress={() => navigate("/users")}
                            className="shadow-lg shadow-primary/30 hover:scale-105 transition-transform font-semibold"
                        >
                            Back to All Users
                        </Button>
                    </CardBody>
                </Card>
            </div>
        )
    }

    // Get user status based on flags
    const getUserStatus = () => {
        if (user.is_banned) return { label: "banned", color: "danger" as const }
        if (!user.is_active) return { label: "inactive", color: "warning" as const }
        return { label: "active", color: "success" as const }
    }

    const status = getUserStatus()

    // Get role color
    const getRoleColor = (roleName: string) => {
        switch (roleName.toLowerCase()) {
            case "admin":
            case "administrator":
                return "primary"
            case "editor":
            case "moderator":
                return "secondary"
            case "viewer":
            case "user":
                return "default"
            default:
                return "default"
        }
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-default-50">
            {/* Header */}
            <header className="border-b border-border bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row min-h-[56px] sm:h-16 py-2 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex items-center gap-3">
                            <Button
                                variant="light"
                                size="sm"
                                onPress={() => navigate(-1)}
                                startContent={<ChevronLeft className="h-4 w-4" />}
                                className="hover:scale-105 transition-transform flex-shrink-0"
                            >
                                <span className="hidden sm:inline">Back</span>
                                <span className="sm:hidden">Back</span>
                            </Button>
                            <Divider orientation="vertical" className="h-6 hidden sm:block" />
                            <div className="flex items-center gap-3">
                                <Avatar
                                    name={user.display_name}
                                    src={user.profile_picture_url || undefined}
                                    className="h-8 w-8"
                                    color={user.roles && user.roles.length > 0 ? getRoleColor(user.roles[0].name) : "default"}
                                />
                                <div>
                                    <h1 className="text-xl font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">{user.display_name}</h1>
                                    <p className="text-xs text-default-500 font-medium">
                                        {user.roles && user.roles.length > 0 ? user.roles.map(r => r.display_name).join(", ") : "No role"}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                            <Chip
                                color={status.color}
                                variant="flat"
                                startContent={
                                    status.label === "active" ? (
                                        <CheckCircle className="h-3 w-3" />
                                    ) : status.label === "inactive" ? (
                                        <Clock className="h-3 w-3" />
                                    ) : (
                                        <XCircle className="h-3 w-3" />
                                    )
                                }
                                className="flex-shrink-0"
                            >
                                {status.label.charAt(0).toUpperCase() + status.label.slice(1)}
                            </Chip>

                            {/* Action Buttons */}
                            <Button
                                size="sm"
                                variant="flat"
                                startContent={<Edit className="h-4 w-4" />}
                                onPress={() => navigate(`/users/${user.user_id}/edit`)}
                                className="rounded flex-1 sm:flex-none"
                            >
                                <span className="hidden sm:inline">Edit</span>
                                <span className="sm:hidden">Edit</span>
                            </Button>
                            {user.is_banned ? (
                                <Button
                                    size="sm"
                                    color="success"
                                    variant="flat"
                                    startContent={<CheckCircle className="h-4 w-4" />}
                                    onPress={onUnbanModalOpen}
                                    className="rounded flex-1 sm:flex-none"
                                >
                                    <span className="hidden sm:inline">Unban</span>
                                    <span className="sm:hidden">Unban</span>
                                </Button>
                            ) : (
                                <Button
                                    size="sm"
                                    color="danger"
                                    variant="flat"
                                    startContent={<Ban className="h-4 w-4" />}
                                    onPress={onBanModalOpen}
                                    className="rounded flex-1 sm:flex-none"
                                >
                                    <span className="hidden sm:inline">Ban</span>
                                    <span className="sm:hidden">Ban</span>
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-7xl">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - User Info */}
                    <div className="lg:col-span-1 space-y-6">
                        {/* Profile Card */}
                        <Card className="border border-border rounded shadow-none">
                            <CardHeader className="border-b border-divider pb-4">
                                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                    <User className="h-4 w-4" />
                                    Profile Information
                                </h3>
                            </CardHeader>
                            <CardBody className="space-y-4 p-6">
                                <div className="flex flex-col items-center gap-4 pb-4 border-b border-divider">
                                    <Avatar
                                        name={user.display_name}
                                        src={user.profile_picture_url || undefined}
                                        className="h-24 w-24 text-large"
                                    />
                                    <div className="text-center">
                                        <h3 className="text-lg font-semibold text-foreground">{user.display_name}</h3>
                                        <p className="text-sm text-default-500">{user.email}</p>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-start gap-3">
                                        <Mail className="h-4 w-4 text-default-400 mt-0.5" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs text-default-500 font-medium">Email Address</p>
                                            <p className="text-sm text-foreground break-all">
                                                {user.email}
                                            </p>
                                        </div>
                                    </div>

                                    {user.phone_number && (
                                        <div className="flex items-start gap-3">
                                            <Phone className="h-4 w-4 text-default-400 mt-0.5" />
                                            <div className="flex-1">
                                                <p className="text-xs text-default-500 font-medium">Phone Number</p>
                                                <p className="text-sm text-foreground">
                                                    {user.phone_number}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-start gap-3">
                                        <Shield className="h-4 w-4 text-default-400 mt-0.5" />
                                        <div className="flex-1">
                                            <p className="text-xs text-default-500 font-medium">Roles</p>
                                            <div className="flex flex-wrap gap-1.5 mt-1">
                                                {user.roles && user.roles.length > 0 ? user.roles.map((role) => (
                                                    <Chip
                                                        key={role.role_id}
                                                        size="sm"
                                                        variant="flat"
                                                        color={getRoleColor(role.name)}
                                                    >
                                                        {role.display_name}
                                                    </Chip>
                                                )) : (
                                                    <span className="text-sm text-default-400">No roles assigned</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <ShieldCheck className="h-4 w-4 text-default-400 mt-0.5" />
                                        <div className="flex-1">
                                            <p className="text-xs text-default-500 font-medium">Verification Status</p>
                                            <Chip
                                                size="sm"
                                                variant="flat"
                                                color={user.is_verified ? "success" : "warning"}
                                                className="mt-1"
                                                startContent={user.is_verified ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                                            >
                                                {user.is_verified ? "Verified" : "Not Verified"}
                                            </Chip>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <Calendar className="h-4 w-4 text-default-400 mt-0.5" />
                                        <div className="flex-1">
                                            <p className="text-xs text-default-500 font-medium">Member Since</p>
                                            <p className="text-sm text-foreground">
                                                {new Date(user.created_at).toLocaleDateString("en-US", {
                                                    year: "numeric",
                                                    month: "long",
                                                    day: "numeric",
                                                })}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <Clock className="h-4 w-4 text-default-400 mt-0.5" />
                                        <div className="flex-1">
                                            <p className="text-xs text-default-500 font-medium">Last Updated</p>
                                            <p className="text-sm text-foreground">
                                                {new Date(user.updated_at).toLocaleDateString("en-US", {
                                                    year: "numeric",
                                                    month: "short",
                                                    day: "numeric",
                                                })}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>

                    {/* Right Column - Permissions and Account Status */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Permissions Card */}
                        <Card className="border border-border rounded shadow-none">
                            <CardHeader className="border-b border-divider pb-4">
                                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                    <Lock className="h-4 w-4" />
                                    Permissions ({user.permissions?.length || 0})
                                </h3>
                            </CardHeader>
                            <CardBody className="p-6">
                                {!user.permissions || user.permissions.length === 0 ? (
                                    <div className="text-center py-12">
                                        <div className="p-3 rounded bg-default-100 w-16 h-16 mx-auto mb-3 flex items-center justify-center">
                                            <Lock className="h-7 w-7 text-default-400" />
                                        </div>
                                        <p className="text-sm text-default-500">No permissions assigned</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                        {user.permissions.map((permission, index) => (
                                            <div
                                                key={index}
                                                className="px-3 py-2 rounded border border-border bg-default-50 text-sm text-foreground font-medium"
                                            >
                                                {permission}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardBody>
                        </Card>

                        {/* Role Management Card */}
                        <ProtectedComponent permission={PERMISSIONS.USER_ASSIGN_ROLE}>
                            <Card className="border border-border rounded shadow-none">
                                <CardHeader className="border-b border-divider pb-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center w-full sm:justify-between gap-3">
                                        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                            <Shield className="h-4 w-4" />
                                            Role Management
                                        </h3>
                                        <Button
                                            size="sm"
                                            variant="flat"
                                            color="primary"
                                            startContent={<ShieldCheck className="h-4 w-4" />}
                                            onPress={onRoleModalOpen}
                                            className="rounded w-full sm:w-auto"
                                        >
                                            <span className="hidden sm:inline">Manage Roles</span>
                                            <span className="sm:hidden">Manage</span>
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardBody className="p-4 sm:p-6">
                                    <div className="space-y-4">
                                        <div className="flex flex-col gap-2">
                                            <p className="text-xs text-default-500 font-medium">Current Roles</p>
                                            <div className="flex flex-wrap gap-1.5 min-h-[2rem]">
                                                {user.roles && user.roles.length > 0 ? user.roles.map((role) => (
                                                    <Chip
                                                        key={role.role_id}
                                                        size="sm"
                                                        variant="flat"
                                                        color={getRoleColor(role.name)}
                                                        className="text-xs"
                                                    >
                                                        {role.display_name}
                                                    </Chip>
                                                )) : (
                                                    <span className="text-sm text-default-400 italic">No roles assigned</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-xs text-default-500 bg-default-50 p-3 rounded border border-border">
                                            <p className="font-medium mb-1">Quick Actions:</p>
                                            <p>Use the "Manage Roles" button to assign or remove multiple roles at once.</p>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </ProtectedComponent>
                        <Card className="border border-border rounded shadow-none">
                            <CardHeader className="border-b border-divider pb-4">
                                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                    <UserCheck className="h-4 w-4" />
                                    Account Status
                                </h3>
                            </CardHeader>
                            <CardBody className="p-6">
                                <div className="grid gap-4">
                                    <div className="flex justify-between items-center p-4 rounded border border-border bg-default-50/50">
                                        <div className="flex items-center gap-2">
                                            <UserCheck className="h-4 w-4 text-default-500" />
                                            <span className="text-sm text-foreground font-medium">Active Status</span>
                                        </div>
                                        <Chip
                                            size="sm"
                                            variant="flat"
                                            color={user.is_active ? "success" : "warning"}
                                            startContent={user.is_active ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                                        >
                                            {user.is_active ? "Active" : "Inactive"}
                                        </Chip>
                                    </div>

                                    <div className="flex justify-between items-center p-4 rounded border border-border bg-default-50/50">
                                        <div className="flex items-center gap-2">
                                            <Mail className="h-4 w-4 text-default-500" />
                                            <span className="text-sm text-foreground font-medium">Email Verification</span>
                                        </div>
                                        <Chip
                                            size="sm"
                                            variant="flat"
                                            color={user.is_verified ? "success" : "warning"}
                                            startContent={user.is_verified ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                                        >
                                            {user.is_verified ? "Verified" : "Not Verified"}
                                        </Chip>
                                    </div>

                                    <div className="flex justify-between items-center p-4 rounded border border-border bg-default-50/50">
                                        <div className="flex items-center gap-2">
                                            <UserX className="h-4 w-4 text-default-500" />
                                            <span className="text-sm text-foreground font-medium">Ban Status</span>
                                        </div>
                                        <Chip
                                            size="sm"
                                            variant="flat"
                                            color={user.is_banned ? "danger" : "success"}
                                            startContent={user.is_banned ? <Ban className="h-3 w-3" /> : <CheckCircle className="h-3 w-3" />}
                                        >
                                            {user.is_banned ? "Banned" : "Not Banned"}
                                        </Chip>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </main>

            {/* Ban Confirmation Modal */}
            <Modal isOpen={isBanModalOpen} onClose={onBanModalClose} classNames={{
                base: 'rounded'
            }}>
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">
                                Ban User
                            </ModalHeader>
                            <ModalBody>
                                <p>
                                    Are you sure you want to ban <strong>{user.display_name}</strong>?
                                    This will prevent them from accessing the system.
                                </p>
                            </ModalBody>
                            <ModalFooter>
                                <Button variant="light" onPress={onClose} className="rounded">
                                    Cancel
                                </Button>
                                <Button
                                    color="danger"
                                    onPress={() => banUser(user.user_id)}
                                    isLoading={isBanning}
                                    className="rounded"
                                >
                                    Ban User
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>

            {/* Unban Confirmation Modal */}
            <Modal isOpen={isUnbanModalOpen} onClose={onUnbanModalClose}>
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">
                                Unban User
                            </ModalHeader>
                            <ModalBody>
                                <p>
                                    Are you sure you want to unban <strong>{user.display_name}</strong>?
                                    This will restore their access to the system.
                                </p>
                            </ModalBody>
                            <ModalFooter>
                                <Button variant="light" onPress={onClose}>
                                    Cancel
                                </Button>
                                <Button
                                    color="success"
                                    onPress={() => unbanUser(user.user_id)}
                                    isLoading={isUnbanning}
                                >
                                    Unban User
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>

            {/* Role Management Modal */}
            <Modal
                isOpen={isRoleModalOpen}
                onClose={onRoleModalClose}
                size="lg"
                scrollBehavior="inside"
                isDismissable={!isAssigningRoles && !isRemovingRoles}
                classNames={{
                    base: `rounded max-h-[90vh] ${isAssigningRoles || isRemovingRoles ? 'pointer-events-none' : ''}`,
                    body: 'px-4 sm:px-6',
                    header: 'px-4 sm:px-6',
                    footer: 'px-4 sm:px-6'
                }}
            >
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1 pb-2">
                                <div className="flex items-center gap-2">
                                    <Shield className="h-5 w-5 text-primary flex-shrink-0" />
                                    <span className="text-sm sm:text-base font-semibold truncate">
                                        Manage Roles for {user.display_name}
                                    </span>
                                </div>
                            </ModalHeader>
                            <ModalBody className="space-y-4 sm:space-y-6 py-2 relative">
                                {/* Permission Check */}
                                {!canManageRoles ? (
                                    <div className="text-center py-8">
                                        <div className="p-4 rounded-full bg-danger/10 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                            <Shield className="h-8 w-8 text-danger" />
                                        </div>
                                        <h3 className="text-lg font-semibold text-foreground mb-2">Access Denied</h3>
                                        <p className="text-sm text-default-500">
                                            You don't have permission to manage user roles.
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        {/* Loading Overlay */}
                                        {(isAssigningRoles || isRemovingRoles) && (
                                            <div className="absolute inset-0 bg-white/80 dark:bg-black/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
                                                <div className="flex flex-col items-center gap-3">
                                                    <Spinner size="lg" color="primary" />
                                                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                                        {isAssigningRoles ? 'Assigning roles...' : 'Removing roles...'}
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Current Roles */}
                                        <div className="space-y-3">
                                            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                                <CheckCircle className="h-4 w-4 text-success flex-shrink-0" />
                                                Current Roles
                                            </h4>
                                            <div className="flex flex-wrap gap-2 min-h-[2rem]">
                                                {user.roles && user.roles.length > 0 ? user.roles.map((role) => (
                                                    <Chip
                                                        key={role.role_id}
                                                        size="sm"
                                                        variant="flat"
                                                        color={getRoleColor(role.name)}
                                                        startContent={<Shield className="h-3 w-3" />}
                                                        className="text-xs"
                                                    >
                                                        {role.display_name}
                                                    </Chip>
                                                )) : (
                                                    <span className="text-sm text-default-400 italic">No roles assigned</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Assign Roles */}
                                        <div className="space-y-3">
                                            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                                <Plus className="h-4 w-4 text-primary flex-shrink-0" />
                                                Assign Roles
                                            </h4>
                                            <div className="space-y-3">
                                                <p className="text-xs text-default-500">Select roles to assign to this user:</p>
                                                <div className={`border border-border rounded-lg p-3 bg-default-50/50 ${isAssigningRoles ? 'opacity-60 pointer-events-none' : ''}`}>
                                                    <CheckboxGroup
                                                        value={selectedRolesToAssign}
                                                        onChange={setSelectedRolesToAssign}
                                                        isDisabled={isAssigningRoles}
                                                        classNames={{
                                                            wrapper: "space-y-2"
                                                        }}
                                                    >
                                                        {roles
                                                            .filter(role => !user.roles?.some(userRole => userRole.role_id === role.roleId))
                                                            .map((role) => (
                                                                <Checkbox
                                                                    key={role.roleId}
                                                                    value={role.roleId}
                                                                    size="sm"
                                                                    isDisabled={isAssigningRoles}
                                                                    classNames={{
                                                                        label: "text-sm font-medium",
                                                                        wrapper: "group"
                                                                    }}
                                                                >
                                                                    <div className="flex items-center gap-2">
                                                                        <Shield className="h-3 w-3 text-default-400" />
                                                                        <span className="truncate">{role.displayName}</span>
                                                                    </div>
                                                                </Checkbox>
                                                            ))}
                                                    </CheckboxGroup>
                                                    {roles.filter(role => !user.roles?.some(userRole => userRole.role_id === role.roleId)).length === 0 && (
                                                        <p className="text-xs text-default-400 italic text-center py-2">
                                                            All available roles are already assigned
                                                        </p>
                                                    )}
                                                    {isAssigningRoles && (
                                                        <div className="flex items-center justify-center gap-2 py-2">
                                                            <Spinner size="sm" color="primary" />
                                                            <span className="text-xs text-primary font-medium">Assigning selected roles...</span>
                                                        </div>
                                                    )}
                                                </div>
                                                {selectedRolesToAssign.length > 0 && (
                                                    <Button
                                                        size="sm"
                                                        color="primary"
                                                        variant="flat"
                                                        startContent={isAssigningRoles ? <Spinner size="sm" /> : <Plus className="h-4 w-4" />}
                                                        onPress={() => {
                                                            handleAssignRoles({ userId: user.user_id, roleIds: selectedRolesToAssign })
                                                            setSelectedRolesToAssign([])
                                                        }}
                                                        isLoading={isAssigningRoles}
                                                        isDisabled={isAssigningRoles || !canManageRoles}
                                                        className="rounded w-full font-medium"
                                                    >
                                                        {isAssigningRoles ? 'Assigning...' : `Assign ${selectedRolesToAssign.length} Role${selectedRolesToAssign.length > 1 ? 's' : ''}`}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Remove Roles */}
                                        {user.roles && user.roles.length > 0 && (
                                            <div className="space-y-3">
                                                <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                                    <XCircle className="h-4 w-4 text-danger flex-shrink-0" />
                                                    Remove Roles
                                                </h4>
                                                <div className="space-y-3">
                                                    <p className="text-xs text-default-500">Select roles to remove from this user:</p>
                                                    <div className={`border border-border rounded-lg p-3 bg-danger-50/30 ${isRemovingRoles ? 'opacity-60 pointer-events-none' : ''}`}>
                                                        <CheckboxGroup
                                                            value={selectedRolesToRemove}
                                                            onChange={setSelectedRolesToRemove}
                                                            isDisabled={isRemovingRoles}
                                                            classNames={{
                                                                wrapper: "space-y-2"
                                                            }}
                                                        >
                                                            {user.roles.map((role) => (
                                                                <Checkbox
                                                                    key={role.role_id}
                                                                    value={role.role_id}
                                                                    size="sm"
                                                                    isDisabled={isRemovingRoles}
                                                                    classNames={{
                                                                        label: "text-sm font-medium",
                                                                        wrapper: "group"
                                                                    }}
                                                                >
                                                                    <div className="flex items-center gap-2">
                                                                        <Shield className="h-3 w-3 text-default-400" />
                                                                        <span className="truncate">{role.display_name}</span>
                                                                    </div>
                                                                </Checkbox>
                                                            ))}
                                                        </CheckboxGroup>
                                                    </div>
                                                    {selectedRolesToRemove.length > 0 && (
                                                        <Button
                                                            size="sm"
                                                            color="danger"
                                                            variant="flat"
                                                            startContent={isRemovingRoles ? <Spinner size="sm" /> : <XCircle className="h-4 w-4" />}
                                                            onPress={() => {
                                                                handleRemoveRoles({ userId: user.user_id, roleIds: selectedRolesToRemove })
                                                                setSelectedRolesToRemove([])
                                                            }}
                                                            isLoading={isRemovingRoles}
                                                            isDisabled={isRemovingRoles || !canManageRoles}
                                                            className="rounded w-full font-medium"
                                                        >
                                                            {isRemovingRoles ? 'Removing...' : `Remove ${selectedRolesToRemove.length} Role${selectedRolesToRemove.length > 1 ? 's' : ''}`}
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </ModalBody>
                            <ModalFooter className="pt-2">
                                <Button
                                    variant="light"
                                    onPress={onClose}
                                    isDisabled={isAssigningRoles || isRemovingRoles}
                                    className="rounded flex-1 sm:flex-none"
                                >
                                    Close
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </div>
    )
}
